import { createApp, markRaw } from "vue/dist/vue.esm-bundler.js";
import { Euler, Quaternion, MathUtils } from "three";
import { validateBytes } from "gltf-validator";
import { createViewport } from "./viewport.js";
import { readGlb, writeGlb } from "./glb.js";
import { EXTENSION, EDITABLE_KINDS, KIND_LABELS, dataOf, nameOf, optionName, importDocument, serializeDocument, exportDocument, validateDocument,
    nodeTree, createSet, duplicateOption, duplicateSet, removeOption, removeSet, moveItem, addSolidMaterial, animationClips, deferredStateIndices } from "./document.js";
import { readDraft, saveDraft, discardDraft, saveViewerTransfer } from "./storage.js";
import { createSampleGlb } from "./sample.js";
import { EDITABLE_TARGET_KINDS, stateTargetRefs, targetRefsForNodes, applyStateTargets } from "./targets.js";
import { filterStateSets } from "./state_filters.js";
import { convertFbx, selectImportFiles } from "./fbx-import.js";
import { translate, loadLanguage, saveLanguage, LANGUAGE_KEY } from "./i18n.js";
import { savePaintMaterial, materialSlots, materialUses, validatePaint, materialSwatch } from "./materials.js";
import { preserveCameraView } from "./camera_view.js";
import { DEFAULT_HIERARCHY_WIDTH, MIN_HIERARCHY_WIDTH, hierarchyWidthLimit, clampHierarchyWidth, loadHierarchyWidth, saveHierarchyWidth } from "./layout.js";
import { hierarchyDropPlan, moveHierarchy, hierarchySelection, duplicateHierarchy, deleteHierarchy } from "./hierarchy.js";
import { insertModel } from "./insert.js";
import { formatTransformValue } from "./object_properties.js";

const clone = value => JSON.parse(JSON.stringify(value));
let sourceBytes = null, viewport = null, runtimeDocument = null, exportedBytes = null, autosaveTimer = null, previewTimer = null;
let saveQueue = Promise.resolve();
let hierarchyScrollFrame = null;
const undoStack = [], redoStack = [];

const app = createApp({
    data: () => ({
        language: loadLanguage({ getItem: key => localStorage.getItem(key) }, navigator.language),
        hierarchyPreferredWidth: loadHierarchyWidth({ getItem: key => localStorage.getItem(key) }),
        workspaceWidth: window.innerWidth, hierarchyResize: null,
        document: null, selectedSetId: null, selectedOptionId: null, selectedNodes: [], partSearch: "", collapsedPaths: [], selections: {}, currentCamera: null, objectTransforms: [],
        setScopeFilter: "all", setKindFilter: "all",
        pendingFbx: null, pendingTextures: [], pendingInsertParent: null, insertionRequest: null, documentGeneration: 0, animationPaused: false,
        draft: null, draftBaseline: "", targetDraft: [], targetBaseline: "[]", draggedNodes: [], targetDragOver: false, hierarchyDrop: null, dragOrigin: null, suppressPartClickUntil: 0, setName: "", rotation: { X: 0, Y: 0, Z: 0 },
        groupTimingMode: "ParentGroupDuration", groupTimingBaseline: "ParentGroupDuration",
        tool: "inspect", tools: [{ value: "inspect", label: "Select", icon: "fa-mouse-pointer" }, { value: "translate", label: "Move", icon: "fa-arrows-alt" }, { value: "rotate", label: "Rotate", icon: "fa-sync-alt" }], axes: ["X", "Y", "Z"],
        mode: "author", busy: false, progress: "", dragging: false, error: "", status: "Import a model to begin", persistenceStatus: "Files stay in your browser", lightingName: "Loading HDR…",
        viewportDirty: false, autoPreview: true, previewedDraftKey: "", previewHasEdits: false, unsaved: false, recovery: null, undoCount: 0, redoCount: 0, thumbnails: {},
        newSetKind: "Activation", newSetName: "", materialEdit: null, materialError: "", newMaterial: {}, automaticMaterialReference: null, materialChoiceEdited: false,
        exportReady: false, exportName: "model-with-states.glb", exportSize: "", exportWarnings: []
    }),
    computed: {
        hierarchyWidth() { return clampHierarchyWidth(this.hierarchyPreferredWidth, this.workspaceWidth); },
        hierarchyMaxWidth() { return hierarchyWidthLimit(this.workspaceWidth); },
        hierarchyMinWidth() { return MIN_HIERARCHY_WIDTH; },
        nodes() { return this.document ? nodeTree(this.document.json) : []; },
        hierarchyNodes() {
            const parents = [], rows = [];
            for (const node of this.nodes) {
                if (node.path.split("/").some(segment => segment.startsWith("__INNOWORKS_MATERIAL"))) continue;
                const parent = parents[node.depth - 1];
                const row = { ...node, parentIndex: parent?.index ?? null, hasChildren: false };
                if (parent) parent.hasChildren = true;
                parents[node.depth] = row; parents.length = node.depth + 1; rows.push(row);
            }
            return rows;
        },
        hierarchyMatches() {
            const query = this.partSearch.trim().toLowerCase();
            if (!query) return null;
            const rows = new Map(this.hierarchyNodes.map(node => [node.index, node])), matches = new Set();
            for (const node of this.hierarchyNodes) {
                if (!node.name.toLowerCase().includes(query)) continue;
                for (let row = node; row; row = rows.get(row.parentIndex)) matches.add(row.index);
            }
            return matches;
        },
        filteredNodes() {
            const collapsed = new Set(this.collapsedPaths), matches = this.hierarchyMatches;
            let hiddenDepth = Infinity;
            return this.hierarchyNodes.filter(node => {
                if (node.depth > hiddenDepth) return false;
                hiddenDepth = Infinity;
                if (matches && !matches.has(node.index)) return false;
                if (collapsed.has(node.path)) hiddenDepth = node.depth;
                return true;
            });
        },
        selectedPaths() { return this.nodes.filter(node => this.selectedNodes.includes(node.index)).map(node => node.path); },
        selectedObjectProperties() {
            const nodes = new Map(this.nodes.map(node => [node.index, node]));
            return this.objectTransforms.filter(transform => this.selectedNodes.includes(transform.index) && nodes.has(transform.index))
                .map(transform => ({ ...transform, name: nodes.get(transform.index).name, path: nodes.get(transform.index).path }));
        },
        canEditParts() { return !!this.document && !this.busy && !this.materialEdit && !this.dragOrigin && this.mode === "author" && !!hierarchySelection(this.document.json, this.selectedNodes); },
        canInsertModel() { return !!this.document && !this.busy && !this.materialEdit && this.mode === "author" && this.selectedNodes.length === 1
            && this.nodes.some(node => node.index === this.selectedNodes[0] && !node.path.split("/").some(name => name.startsWith("__INNOWORKS_MATERIAL"))); },
        stateKinds() { return Object.keys(KIND_LABELS).filter(kind => kind !== "Unknown" || this.document?.sets.some(set => set.kind === "Unknown")); },
        filteredSets() {
            return filterStateSets(this.document?.sets ?? [], { scope: this.setScopeFilter, kind: this.setKindFilter, paths: this.selectedPaths });
        },
        selectedSet() { return this.document?.sets.find(set => set.id === this.selectedSetId); },
        selectedOption() { return this.selectedSet?.options.find(option => option.id === this.selectedOptionId); },
        stateThumbnail() {
            const value = this.selectedSet && dataOf(this.selectedSet)?.ThumbnailBase64;
            return typeof value === "string" && value.length ? value.startsWith("data:") ? value : `data:image/png;base64,${value}` : null;
        },
        setIndex() { return this.document?.sets.findIndex(set => set.id === this.selectedSetId) ?? -1; },
        optionIndex() { return this.selectedSet?.options.findIndex(option => option.id === this.selectedOptionId) ?? -1; },
        groupTimingChanged() { return this.selectedSet?.kind === "Group" && this.groupTimingMode !== this.groupTimingBaseline; },
        showTransition() { return this.selectedSet?.kind !== "Animation" && (this.selectedSet?.kind !== "Group" || this.groupTimingMode === "ParentGroupDuration"); },
        dirty() { return !!this.draft && (JSON.stringify(this.draft) !== this.draftBaseline || JSON.stringify(this.targetDraft) !== this.targetBaseline || this.groupTimingChanged); },
        draftKey() { return JSON.stringify([this.draft, this.targetDraft, this.selectedSet?.kind === "Group" ? this.groupTimingMode : null]); },
        optionEditsPending() { return this.dirty || this.viewportDirty || this.previewHasEdits; },
        totalOptions() { return this.document?.sets.reduce((sum, set) => sum + set.options.length, 0) ?? 0; },
        previewSets() { return this.document?.sets.filter(set => set.kind !== "Unknown" && dataOf(set).UiVisibility !== "Hidden") ?? []; },
        editableTargets() { return EDITABLE_TARGET_KINDS.includes(this.selectedSet?.kind); },
        embeddedClips() { return this.document ? animationClips(this.document) : []; },
        availableClips() { return this.document ? animationClips(this.document, this.selectedSet) : []; },
        selectedClip() { return this.availableClips.find(clip => clip.index === this.draft?.raw.AnimationIndex); },
        targetRows() {
            if (!this.selectedSet) return [];
            const refs = this.editableTargets ? this.targetDraft : stateTargetRefs(this.selectedSet);
            return refs.map((ref, index) => {
                const matches = this.nodes.filter(node => node.path === ref.Path), node = matches.length === 1 ? matches[0] : null;
                return { ...ref, key: index, nodeIndex: node?.index, name: node?.name || ref.Path || this.t("Model root"),
                    pathLabel: ref.Path || this.t("Model root"), missing: !node,
                    slots: this.selectedSet.kind === "Material" ? materialSlots(this.document, ref.Path) : [] };
            });
        },
        targetNodeIndices() { return [...new Set(this.targetRows.filter(row => !row.missing).map(row => row.nodeIndex))]; },
        stateContext() {
            if (!this.selectedSet) return { name: "", title: "" };
            const refs = this.targetHint ? this.targetRows : [{ Path: this.selectedSet.raw.Path ?? "" }];
            const targets = [...new Map(refs.map(ref => [ref.Path, ref])).values()].map(ref => {
                const node = this.nodes.find(item => item.path === ref.Path);
                return { name: ref.name || node?.name || ref.Path || this.t("Model root"), path: ref.Path };
            });
            const name = targets.length > 1 ? this.t("{name} +{count} more", { name: targets[0].name, count: targets.length - 1 })
                : targets[0]?.name || this.t("No targets");
            const paths = targets.map(target => target.path || target.name).join(", ") || name;
            return { name, title: `${paths} › ${this.kindLabel(this.selectedSet)}` };
        },
        targetHint() {
            return { Transform: "One part, shared by every option. Drop a part to replace it; existing poses are kept.",
                Material: "Mesh parts, shared by every option. Drop parts to add them.",
                Activation: "Parts controlled by this State. Check the parts visible in this option. Drop parts to add them.",
                Camera: "Reference space for the saved camera poses. Reference editing requires desktop Create.",
                Animation: "Clips animate their embedded part bindings. Choose the clip and playback settings below.",
                Light: "Light targets are preserved; edit them in desktop Create." }[this.selectedSet?.kind] || "";
        },
        groupSets() { return this.document?.sets.filter(set => set.id !== this.selectedSetId && set.kind !== "Unknown") ?? []; },
        materials() {
            if (!this.document) return [];
            return [...(this.document.json.materials ?? []).map((material, i) => ({ key: `gltf:${i}`, name: material.name || `Material ${i + 1}`, raw: { MaterialName: material.name } })),
                ...(this.document.json.extensions[EXTENSION].Materials ?? []).map(material => ({ key: `record:${material.Id}`, name: material.Name, raw: { MaterialId: material.Id, MaterialName: material.Name, ShaderName: material.ShaderName } }))];
        },
        materialKey() {
            const value = this.draft?.raw;
            if (!value) return "";
            return this.materials.find(material => Number.isInteger(value.MaterialId) ? material.raw.MaterialId === value.MaterialId : material.raw.MaterialName === value.MaterialName)?.key ?? "";
        }
    },
    watch: {
        partSearch(value) {
            const query = value.trim().toLowerCase();
            if (query) this.expandHierarchyAncestors(this.hierarchyNodes.filter(node => node.name.toLowerCase().includes(query)).map(node => node.index));
        },
        stateKinds(kinds) { if (this.setKindFilter !== "all" && !kinds.includes(this.setKindFilter)) this.setKindFilter = "all"; },
        draftKey: { flush: "post", handler() {
            // Wait for text/number fields to finish so previewing never interrupts typing.
            if (!window.document.activeElement?.matches("input:not([type=checkbox]), textarea")) this.queueDraftPreview();
        } },
        autoPreview(value) { if (value) this.queueDraftPreview(); else clearTimeout(previewTimer); }
    },
    methods: {
        toggleBranch(index) {
            if (this.busy || this.dragOrigin) return;
            const node = this.hierarchyNodes.find(node => node.index === index);
            if (!node?.hasChildren) return;
            this.collapsedPaths = this.collapsedPaths.includes(node.path)
                ? this.collapsedPaths.filter(path => path !== node.path) : [...this.collapsedPaths, node.path];
        },
        expandHierarchyAncestors(indices) {
            const rows = new Map(this.hierarchyNodes.map(node => [node.index, node])), expanded = new Set();
            for (const index of indices) {
                for (let row = rows.get(rows.get(index)?.parentIndex); row; row = rows.get(row.parentIndex)) expanded.add(row.path);
            }
            if (expanded.size) this.collapsedPaths = this.collapsedPaths.filter(path => !expanded.has(path));
        },
        queueDraftPreview() {
            clearTimeout(previewTimer);
            if (!this.autoPreview || this.busy || this.mode !== "author" || !this.draft || this.viewportDirty || this.materialEdit
                || this.draftKey === this.previewedDraftKey) return;
            previewTimer = setTimeout(() => this.previewDraft(), 200);
        },
        async finishOptionEdits() {
            if (this.busy) return false;
            return this.optionEditsPending ? this.updateOption() : true;
        },
        setHierarchyWidth(width) {
            this.hierarchyPreferredWidth = clampHierarchyWidth(width, this.workspaceWidth);
            saveHierarchyWidth({ setItem: (key, value) => localStorage.setItem(key, value) }, this.hierarchyPreferredWidth);
        },
        startHierarchyResize(event) {
            if (event.button !== 0 || !event.isPrimary) return;
            event.preventDefault();
            this.hierarchyResize = { pointerId: event.pointerId, x: event.clientX, width: this.hierarchyWidth };
            event.currentTarget.setPointerCapture(event.pointerId);
        },
        moveHierarchyResize(event) {
            if (this.hierarchyResize?.pointerId !== event.pointerId) return;
            this.setHierarchyWidth(this.hierarchyResize.width + event.clientX - this.hierarchyResize.x);
        },
        stopHierarchyResize(event) {
            if (this.hierarchyResize?.pointerId !== event.pointerId) return;
            this.hierarchyResize = null;
            if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
        },
        hierarchyResizeKey(event) {
            const widths = { ArrowLeft: this.hierarchyWidth - (event.shiftKey ? 40 : 10), ArrowRight: this.hierarchyWidth + (event.shiftKey ? 40 : 10),
                Home: MIN_HIERARCHY_WIDTH, End: this.hierarchyMaxWidth, Enter: DEFAULT_HIERARCHY_WIDTH };
            if (widths[event.key] === undefined) return;
            event.preventDefault(); this.setHierarchyWidth(widths[event.key]);
        },
        resetHierarchyWidth() { this.setHierarchyWidth(DEFAULT_HIERARCHY_WIDTH); },
        nameOf, dataOf, optionName, formatTransformValue,
        refreshObjectReadout(transforms) {
            if (JSON.stringify(transforms) !== JSON.stringify(this.objectTransforms)) this.objectTransforms = transforms;
        },
        materialSwatch(option) { return materialSwatch(this.document, option.raw); },
        t(value, parameters) { return translate(this.language, value, parameters); },
        changeLanguage(value) {
            if (!["en", "ja"].includes(value)) return;
            this.language = value; window.document.documentElement.lang = value;
            if (!saveLanguage(value, { setItem: (key, item) => localStorage.setItem(key, item) })) this.error = "Language could not be saved in this browser.";
        },
        kindLabel(set) { return this.t(KIND_LABELS[set.kind] || set.kind); },
        isEditable: set => EDITABLE_KINDS.includes(set.kind),
        formatDate(value) { return new Date(value).toLocaleString(this.language === "ja" ? "ja-JP" : "en-US"); },
        thumbnailFor(option) {
            const raw = this.draft?.id === option.id ? this.draft.raw : option.raw;
            return raw.ThumbnailBase64?.startsWith("data:") ? raw.ThumbnailBase64 : this.thumbnails[option.id];
        },
        clearError() { this.error = ""; },
        cleanRequired() {
            if (this.busy) return false;
            if (this.optionEditsPending) { this.error = "Apply or revert the pending edits above the option cards before continuing."; return false; }
            return true;
        },
        async run(message, action) {
            if (this.busy) return false;
            this.busy = true; this.progress = message; this.error = "";
            try { await action(); return true; }
            catch (error) { console.error("Model Vista Create:", error); this.error = error.message || String(error); return false; }
            finally { this.busy = false; }
        },
        snapshot() { return { document: clone(this.document), sourceBytes, selectedSetId: this.selectedSetId, selectedOptionId: this.selectedOptionId, selectedNodes: [...this.selectedNodes], selections: clone(this.selections) }; },
        isDeferred(set = this.selectedSet, doc = this.document) { return (doc?.deferredStateIds ?? []).includes(set?.id); },
        remember(before) { undoStack.push(before); if (undoStack.length > 35) undoStack.shift(); redoStack.length = 0; this.syncHistory(); },
        previewSelections(doc = this.document) {
            const selected = {};
            doc.sets.forEach((set, index) => {
                const option = set.options.findIndex(option => option.id === this.selections[set.id]);
                if (option >= 0 && !this.isDeferred(set, doc)) selected[index] = option;
            });
            return selected;
        },
        syncHistory() { this.undoCount = undoStack.length; this.redoCount = redoStack.length; },
        scheduleSave() {
            clearTimeout(autosaveTimer); this.persistenceStatus = "Saving draft…";
            autosaveTimer = setTimeout(() => this.persist(), 350);
        },
        persist() {
            if (!this.document || !sourceBytes) return Promise.resolve(false);
            const record = { document: clone(this.document), sourceBytes, updated: Date.now() };
            saveQueue = saveQueue.catch(() => {}).then(() => saveDraft(record)).then(() => { this.persistenceStatus = "Draft saved on this device"; return true; }).catch(error => {
                console.warn("Create draft storage:", error); this.persistenceStatus = "Draft could not be saved — export to keep your work";
                return false;
            });
            return saveQueue;
        },
        async reloadDocument(doc, { reset = false, reframe = false } = {}) {
            const source = readGlb(sourceBytes);
            const bytes = writeGlb(source, serializeDocument(doc));
            const selected = reset ? {} : this.previewSelections(doc);
            const previousRuntimeDocument = runtimeDocument;
            runtimeDocument = doc;
            try { await viewport.load(bytes, { reframe, selections: selected, applyInitialCamera: reset, deferredStateIndices: deferredStateIndices(doc) }); }
            catch (error) { runtimeDocument = previousRuntimeDocument; throw error; }
            this.readRuntimeState(doc);
        },
        readRuntimeState(doc = this.document) {
            const state = viewport.getState(), selections = {}, thumbnails = {};
            for (const entry of state?.innoWorksRuntime?.values() ?? []) {
                const index = Number(entry.id.split("|").at(-2)), set = doc.sets[index];
                if (!set) continue;
                if (!entry.authoringDeferred) {
                    selections[set.id] = set.options[entry.currentOption]?.id;
                    doc.deferredStateIds = (doc.deferredStateIds ?? []).filter(id => id !== set.id);
                }
                entry.options.forEach(option => { if (set.options[option.index]) thumbnails[set.options[option.index].id] = option.thumbnail; });
            }
            this.selections = selections; this.thumbnails = thumbnails;
        },
        setDraft() {
            clearTimeout(previewTimer);
            this.automaticMaterialReference = null; this.materialChoiceEdited = false;
            this.animationPaused = !!viewport?.getState()?.animationPlaybackPaused;
            this.setName = this.selectedSet ? nameOf(this.selectedSet) : "";
            this.draft = this.selectedOption ? clone(this.selectedOption) : null;
            if (this.draft) {
                this.draft.raw.TransitionDuration = this.draft.raw.TransitionDuration ?? 0; this.draft.raw.EaseMode = this.draft.raw.EaseMode ?? "Linear";
                if (["Transform", "Camera"].includes(this.selectedSet.kind)) {
                    this.draft.raw.LocalPosition = this.draft.raw.LocalPosition ?? { X: 0, Y: 0, Z: 0 };
                    this.draft.raw.LocalRotation = this.draft.raw.LocalRotation ?? { X: 0, Y: 0, Z: 0, W: 1 };
                    const q = this.draft.raw.LocalRotation, euler = new Euler().setFromQuaternion(new Quaternion(q.X, q.Y, q.Z, q.W));
                    this.rotation = { X: MathUtils.radToDeg(euler.x), Y: MathUtils.radToDeg(euler.y), Z: MathUtils.radToDeg(euler.z) };
                }
                if (this.selectedSet.kind === "Activation") this.draft.raw.TargetPaths = this.draft.raw.TargetPaths ?? [];
                if (this.selectedSet.kind === "Animation") {
                    const raw = this.draft.raw;
                    raw.AnimationIndex = raw.AnimationIndex ?? -1; raw.Speed = raw.Speed ?? 1;
                    raw.Loop = raw.Loop ?? true; raw.CrossFadeDuration = raw.CrossFadeDuration ?? 0.25;
                }
            }
            this.draftBaseline = JSON.stringify(this.draft);
            this.targetDraft = this.selectedSet && this.editableTargets ? clone(stateTargetRefs(this.selectedSet)) : [];
            this.targetBaseline = JSON.stringify(this.targetDraft); this.viewportDirty = false;
            this.groupTimingMode = this.selectedSet?.kind === "Group" && String(dataOf(this.selectedSet).TransitionTimingMode).toLowerCase() === "linkedstatedurations" ? "LinkedStateDurations" : "ParentGroupDuration";
            this.groupTimingBaseline = this.groupTimingMode;
            this.previewedDraftKey = this.draftKey; this.previewHasEdits = false;
        },
        async commit(change, after = {}) {
            if (this.busy) return false;
            return this.run(after.progress || "Applying State changes…", () => preserveCameraView(viewport, async () => {
                const before = this.snapshot(), next = clone(this.document);
                const result = await change(next);
                const issues = validateDocument(next);
                if (issues.errors.length) throw new Error(issues.errors.join("\n"));
                const previousSelections = clone(this.selections), previousSource = sourceBytes;
                if (result?.sourceBytes) sourceBytes = result.sourceBytes;
                try { if (!after.reusePreview) await this.reloadDocument(next); else runtimeDocument = next; }
                catch (error) { this.selections = previousSelections; sourceBytes = previousSource; throw error; }
                this.remember(before);
                this.document = markRaw(next);
                if (result?.selectedNodes) this.restorePartSelection(result.selectedNodes);
                if (after.revealParts) this.partSearch = "";
                this.selectedSetId = after.setId || result?.setId || this.selectedSetId;
                if (!next.sets.some(set => set.id === this.selectedSetId)) this.selectedSetId = next.sets[0]?.id ?? null;
                this.selectedOptionId = after.optionId || result?.optionId || this.selectedOptionId;
                if (!this.selectedSet?.options.some(option => option.id === this.selectedOptionId)) this.selectedOptionId = this.selectedSet?.defaultId ?? null;
                if (this.selectedOption && (after.activateOption || this.selections[this.selectedSetId] === this.selectedOptionId) && !this.isDeferred()) viewport.apply(this.setIndex, this.optionIndex, true);
                this.readRuntimeState(); this.setDraft(); this.syncHistory(); this.unsaved = true; this.exportReady = false;
                this.status = after.message || "State changes saved to draft"; this.scheduleSave();
            }));
        },
        async loadBytes(bytes, filename, restored = null, { importReport = null, replaceConfirmed = false } = {}) {
            if (!replaceConfirmed && this.document && (this.unsaved || this.dirty || this.viewportDirty) && !confirm(this.t("Replace this model? Export it first if you want to keep a separate copy of your edits."))) return;
            return this.run("Opening model…", async () => {
                const next = restored ? clone(restored) : importDocument(bytes, filename);
                if (importReport) next.importReport = clone(importReport);
                if (next.version !== 1 || !Array.isArray(next.sets)) throw new Error("This draft version cannot be opened.");
                const issues = validateDocument(next);
                if (issues.errors.length) throw new Error(issues.errors.join("\n"));
                const previousSource = sourceBytes;
                sourceBytes = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
                try { await this.reloadDocument(next, { reset: true, reframe: true }); }
                catch (error) { sourceBytes = previousSource; throw error; }
                this.document = markRaw(next); this.documentGeneration++; this.selectedSetId = next.sets[0]?.id ?? null;
                this.selectedOptionId = this.selectedSet?.defaultId ?? null;
                this.selectedNodes = []; this.collapsedPaths = []; viewport.select([]); this.tool = "inspect"; viewport.setTool("inspect");
                this.setScopeFilter = "all"; this.setKindFilter = "all";
                this.mode = "author"; viewport.setPreview(false); this.recovery = null;
                undoStack.length = 0; redoStack.length = 0; this.syncHistory(); this.setDraft(); this.unsaved = !!restored;
                this.exportReady = false; this.status = `${filename} opened · ${next.sets.length} State sets`;
                this.scheduleSave();
            });
        },
        chooseFile() { if (!this.busy) this.$refs.fileInput.click(); },
        chooseInsertFile() {
            if (!this.canInsertModel) return;
            this.insertionRequest = { parentIndex: this.selectedNodes[0], generation: this.documentGeneration };
            this.$refs.insertInput.click();
        },
        async insertFileSelected(event) {
            const files = [...(event.target.files ?? [])], request = this.insertionRequest;
            event.target.value = ""; this.insertionRequest = null;
            if (!files.length || !request) return;
            if (request.generation !== this.documentGeneration) { this.error = "The model changed. Select a part and try inserting again."; return; }
            if (files.length === 1 && /\.fbx$/i.test(files[0].name)) {
                this.pendingFbx = markRaw(files[0]); this.pendingTextures = []; this.pendingInsertParent = request;
                this.$refs.fbxImportDialog.showModal();
            } else await this.openFiles(files, request);
        },
        async fileSelected(event) {
            const files = [...(event.target.files ?? [])]; event.target.value = "";
            if (!files.length) return;
            if (files.length === 1 && /\.fbx$/i.test(files[0].name)) {
                this.pendingFbx = markRaw(files[0]); this.pendingTextures = []; this.pendingInsertParent = null;
                this.$refs.fbxImportDialog.showModal();
            } else await this.openFiles(files);
        },
        texturesSelected(event) {
            const files = [...(event.target.files ?? [])]; event.target.value = "";
            if (files.length) this.pendingTextures = files.map(file => markRaw(file));
        },
        async importPendingFbx() {
            if (!this.pendingFbx) return;
            const files = [this.pendingFbx, ...this.pendingTextures], insertion = this.pendingInsertParent;
            this.$refs.fbxImportDialog.close();
            await this.openFiles(files, insertion);
        },
        async openFiles(files, insertion = null) {
            if (this.busy) return;
            try {
                const { model, textures } = selectImportFiles(files);
                if (insertion && (insertion.generation !== this.documentGeneration || !this.document)) throw new Error("The model changed. Select a part and try inserting again.");
                if (!insertion && this.document && (this.unsaved || this.dirty || this.viewportDirty) && !confirm(this.t("Replace this model? Export it first if you want to keep a separate copy of your edits."))) return;
                let imported;
                const ready = await this.run("Reading model…", async () => {
                    imported = /\.fbx$/i.test(model.name)
                        ? await convertFbx(model, textures, message => { this.progress = message; })
                        : { bytes: new Uint8Array(await model.arrayBuffer()), filename: model.name };
                });
                if (!ready) return;
                if (insertion) await this.insertBytes(imported.bytes, imported.filename, insertion.parentIndex, imported.report);
                else await this.loadBytes(imported.bytes, imported.filename, null, { importReport: imported.report, replaceConfirmed: true });
            }
            catch (error) { this.error = error.message; }
        },
        async insertBytes(bytes, filename, parentIndex, importReport = null) {
            if (!this.document || this.materialEdit || this.mode !== "author" || !await this.finishOptionEdits()) return false;
            const assertValid = async value => {
                const validation = await validateBytes(value, { maxIssues: 50 });
                if (validation.issues.numErrors) throw new Error("GLB validation failed:\n" + validation.issues.messages.filter(message => message.severity === 0)
                    .map(message => `${message.pointer || "Model"}: ${message.message}`).join("\n"));
            };
            return this.commit(async doc => {
                await assertValid(bytes);
                const result = insertModel(doc, sourceBytes, bytes, filename, parentIndex, importReport);
                await assertValid(result.sourceBytes);
                return result;
            }, { progress: "Inserting model…", message: "Model inserted — Undo removes it", revealParts: true });
        },
        fileDragOver(event) { if ([...(event.dataTransfer?.types ?? [])].includes("Files")) { event.preventDefault(); this.dragging = true; } },
        handleDrop(event) { if (![...(event.dataTransfer?.types ?? [])].includes("Files")) return; event.preventDefault(); this.dragging = false; this.openFiles([...(event.dataTransfer?.files ?? [])]); },
        loadSample() { return this.loadBytes(createSampleGlb(), "Inspection-unit.glb"); },
        restoreDraft() { return this.loadBytes(this.recovery.sourceBytes, this.recovery.document.filename, this.recovery.document); },
        async removeDraft() { try { await discardDraft(); this.recovery = null; } catch (error) { this.error = error.message; } },
        selectNode(index, additive = false) {
            if (this.busy || this.mode !== "author" || Date.now() < this.suppressPartClickUntil) return;
            this.selectedNodes = additive ? this.selectedNodes.includes(index) ? this.selectedNodes.filter(value => value !== index) : [...this.selectedNodes, index] : [index];
            this.expandHierarchyAncestors(this.selectedNodes);
            viewport.select(this.selectedNodes);
        },
        restorePartSelection(indices) {
            const available = new Set(this.nodes.map(node => node.index));
            this.selectedNodes = [...new Set(indices)].filter(index => available.has(index));
            this.expandHierarchyAncestors(this.selectedNodes);
            viewport.select(this.selectedNodes);
        },
        async duplicateParts() {
            if (!this.canEditParts) return false;
            const indices = [...this.selectedNodes];
            if (!await this.finishOptionEdits()) return false;
            const parts = viewport.captureHierarchyParts?.() ?? null;
            return this.commit(doc => duplicateHierarchy(doc, indices, parts), { message: "Parts duplicated — Undo restores the previous hierarchy" });
        },
        async deleteParts() {
            if (!this.canEditParts) return false;
            const indices = [...this.selectedNodes];
            if (!await this.finishOptionEdits()) return false;
            return this.commit(doc => deleteHierarchy(doc, indices), { message: "Parts deleted — Undo restores them" });
        },
        setTool(value) { if (this.busy) return; this.tool = value; viewport.setTool(value); },
        frameModel() { viewport.frame(); }, frameSelection() { if (this.selectedNodes.length) viewport.frameSelected(); else viewport.frame(); },
        async selectSet(id) {
            if (!await this.finishOptionEdits()) return;
            this.selectedSetId = id; this.selectedOptionId = this.selections[id] || this.selectedSet.defaultId;
            this.setDraft();
        },
        async selectOption(id) {
            if (!await this.finishOptionEdits()) return;
            const before = this.snapshot();
            this.selectedOptionId = id;
            this.activateOption(this.selectedSet, this.selectedOption, before); this.setDraft();
            this.status = `${nameOf(this.selectedSet)} / ${optionName(this.selectedOption)}`;
        },
        activateOption(set, option, before = this.snapshot()) {
            const changed = this.selections[set.id] !== option.id || this.isDeferred(set);
            if (!viewport.apply(this.document.sets.indexOf(set), set.options.indexOf(option))) return;
            this.readRuntimeState();
            if (changed) { this.remember(before); this.unsaved = true; this.scheduleSave(); }
        },
        playOption(set, option) {
            if (!this.cleanRequired()) return;
            this.activateOption(set, option);
            this.status = `${nameOf(set)} / ${optionName(option)}`;
        },
        async setMode(value) {
            if (!this.document || !await this.finishOptionEdits()) return;
            this.mode = value; viewport.setPreview(value === "preview"); this.tool = "inspect"; viewport.setTool("inspect");
            if (value === "author") { this.selectedOptionId = this.selections[this.selectedSetId] || this.selectedSet?.defaultId || null; this.setDraft(); }
            this.status = value === "preview" ? "Previewing your model's State controls" : "Authoring States";
        },
        resetPreview() { return this.run("Restoring initial options…", async () => { await this.reloadDocument(this.document, { reset: true }); this.status = "Initial options restored"; }); },
        openNewSet() {
            if (this.busy || (this.selectedSet && !this.cleanRequired())) return;
            if (EDITABLE_KINDS.includes(this.setKindFilter)) this.newSetKind = this.setKindFilter;
            this.newSetName = ""; this.$refs.newSetDialog.showModal();
        },
        async addSet() {
            const kind = this.newSetKind, paths = this.selectedPaths;
            let transform, camera, material;
            try {
                if (kind === "Transform" && paths.length === 1) transform = viewport.captureTransform(paths[0]);
                if (kind === "Camera") camera = viewport.captureCamera();
                if (kind === "Material") {
                    if (this.nodes.filter(node => this.selectedNodes.includes(node.index)).some(node => node.mesh === undefined)) throw new Error("Select mesh parts for a Material State.");
                    material = paths.length ? viewport.captureMaterial(paths[0]) : null;
                    material = material || this.materials[0]?.raw;
                }
            } catch (error) { this.error = error.message; this.$refs.newSetDialog.close(); return; }
            this.$refs.newSetDialog.close();
            const created = await this.commit(doc => {
                if (kind === "Material" && !material) material = addSolidMaterial(doc, "Default finish", [0.6, 0.65, 0.7], 0, 0.4);
                const set = createSet(doc, kind, { name: this.newSetName, paths, transform, camera, material });
                return { setId: set.id, optionId: set.defaultId };
            }, { message: "State set created" });
            if (created) {
                if (this.setKindFilter !== "all" && this.setKindFilter !== kind) this.setKindFilter = "all";
                if (this.setScopeFilter === "selected" && !this.filteredSets.some(set => set.id === this.selectedSetId)) this.setScopeFilter = "all";
            }
        },
        renameSet() {
            if (!this.cleanRequired()) { this.setName = nameOf(this.selectedSet); return; }
            const name = this.setName.trim(); if (!name) { this.setName = nameOf(this.selectedSet); return; }
            this.commit(doc => { dataOf(doc.sets[this.setIndex]).ModelVistaName = name; });
        },
        setVisibility(visible) { if (!this.cleanRequired()) return; this.commit(doc => { dataOf(doc.sets[this.setIndex]).UiVisibility = visible ? "Visible" : "Hidden"; }); },
        moveSet(direction) { if (this.cleanRequired()) this.commit(doc => moveItem(doc.sets, this.selectedSetId, direction)); },
        copySet() { if (this.cleanRequired()) this.commit(doc => { const set = duplicateSet(doc, this.selectedSetId); return { setId: set.id, optionId: set.defaultId }; }); },
        deleteSet() { if (this.cleanRequired()) this.commit(doc => removeSet(doc, this.selectedSetId)); },
        setDefault() { if (this.cleanRequired()) this.commit(doc => { const set = doc.sets[this.setIndex]; set.defaultId = this.selectedOptionId; set.raw.ApplyOnAwake = true; }); },
        moveOption(direction) { if (this.cleanRequired()) this.commit(doc => moveItem(doc.sets[this.setIndex].options, this.selectedOptionId, direction)); },
        copyOption() { if (this.cleanRequired()) this.commit(doc => { const option = duplicateOption(doc.sets[this.setIndex], this.selectedOptionId); return { optionId: option.id }; }); },
        deleteOption() { if (this.cleanRequired()) this.commit(doc => removeOption(doc, doc.sets[this.setIndex], this.selectedOptionId)); },
        rotationChanged() {
            const q = new Quaternion().setFromEuler(new Euler(...this.axes.map(axis => MathUtils.degToRad(this.rotation[axis]))));
            this.draft.raw.LocalRotation = { X: q.x, Y: q.y, Z: q.z, W: q.w };
        },
        refreshCameraReadout() {
            if (!viewport || this.busy || this.selectedSet?.kind !== "Camera") return;
            try {
                const pose = viewport.captureCamera(dataOf(this.selectedSet).ReferenceTransformPath ?? this.selectedSet.raw.Path ?? "");
                const q = pose.LocalRotation, e = new Euler().setFromQuaternion(new Quaternion(q.X, q.Y, q.Z, q.W), "XYZ");
                const rounded = value => Number(value.toFixed(3));
                const view = { fieldOfView: rounded(pose.FieldOfView), orthographic: pose.Orthographic, orthographicSize: rounded(pose.OrthographicSize),
                    position: Object.fromEntries(this.axes.map(axis => [axis, rounded(pose.LocalPosition[axis])])),
                    rotation: { X: rounded(MathUtils.radToDeg(e.x)), Y: rounded(MathUtils.radToDeg(e.y)), Z: rounded(MathUtils.radToDeg(e.z)) } };
                if (JSON.stringify(view) !== JSON.stringify(this.currentCamera)) this.currentCamera = view;
            } catch { this.currentCamera = null; }
        },
        changeCameraLens(lens) {
            try {
                viewport.setCameraLens(lens); this.refreshCameraReadout();
                this.viewportDirty = true; this.error = "";
                this.status = "Camera changed — Apply captures this view";
            } catch (error) { this.error = error.message; }
        },
        captureValues(option) {
            const kind = this.selectedSet.kind, raw = option.raw;
            if (kind === "Transform") {
                if (!this.targetDraft.length) throw new Error("Assign a target before capturing its transform.");
                Object.assign(raw, viewport.captureTransform(this.targetDraft[0].Path));
            }
            if (kind === "Camera") Object.assign(raw, viewport.captureCamera(dataOf(this.selectedSet).ReferenceTransformPath ?? this.selectedSet.raw.Path ?? ""), { ApplyPosition: true, ApplyRotation: true, ApplyProjection: true, ApplyFieldOfView: true });
            if (kind === "Activation") {
                const visible = viewport.getState().nodeIndexToObject;
                raw.TargetPaths = this.targetRows.filter(row => visible.get(row.nodeIndex)?.visible).map(row => row.Path);
            }
            if (kind === "Group") option.links = this.document.sets.filter(set => set.id !== this.selectedSetId && set.kind !== "Group" && set.kind !== "Unknown").map(set => ({ raw: {}, setId: set.id, optionId: this.selections[set.id] || set.defaultId }));
            return option;
        },
        captureCurrent() {
            try {
                this.captureValues(this.draft);
                if (this.draft.raw.LocalRotation) {
                    const q = this.draft.raw.LocalRotation, e = new Euler().setFromQuaternion(new Quaternion(q.X, q.Y, q.Z, q.W));
                    this.rotation = { X: MathUtils.radToDeg(e.x), Y: MathUtils.radToDeg(e.y), Z: MathUtils.radToDeg(e.z) };
                }
                this.viewportDirty = false; this.status = "Captured — Apply keeps this change";
                return true;
            }
            catch (error) { this.error = error.message; return false; }
        },
        async captureNew() {
            if (this.busy || !this.selectedSet || !this.selectedOption) return;
            let captured;
            try { captured = this.captureValues(clone(this.draft)); captured.raw.ThumbnailBase64 = viewport.thumbnail(); delete captured.raw.ThumbnailImageIndex; }
            catch (error) { this.error = error.message; return; }
            await this.commit(doc => {
                const set = doc.sets[this.setIndex], option = duplicateOption(set, this.selectedOptionId);
                option.raw = captured.raw; option.raw.DisplayName = `${nameOf(set)} ${set.options.length}`;
                if (captured.links) option.links = captured.links;
                applyStateTargets(set, clone(this.targetDraft));
                return { optionId: option.id };
            }, { message: "Current appearance captured as a new option" });
        },
        captureThumbnail() {
            if (this.busy || !this.draft) return;
            try {
                this.draft.raw.ThumbnailBase64 = viewport.thumbnail(); delete this.draft.raw.ThumbnailImageIndex;
                this.error = ""; this.status = "Thumbnail updated — Apply keeps this change";
            } catch (error) { this.error = "Thumbnail could not be captured: " + error.message; }
        },
        captureSetThumbnail() {
            if (this.busy || !this.selectedSet || this.selectedSet.kind === "Unknown" || this.materialEdit) return false;
            try {
                const thumbnail = viewport.thumbnail(), next = clone(this.document);
                const data = dataOf(next.sets[this.setIndex]);
                data.ThumbnailBase64 = thumbnail; delete data.ThumbnailImageIndex;
                // This metadata edit must keep the current view and pending option draft.
                this.remember(this.snapshot()); this.document = markRaw(next);
                this.unsaved = true; this.exportReady = false; this.error = "";
                this.status = "State thumbnail saved to draft"; this.scheduleSave();
                return true;
            } catch (error) { this.error = "Thumbnail could not be captured: " + error.message; return false; }
        },
        revealTargets(indices) {
            if (this.busy || !indices.length) return;
            this.partSearch = ""; this.selectedNodes = [...indices]; this.tool = "inspect";
            this.expandHierarchyAncestors(indices);
            viewport.setTool("inspect"); viewport.select(indices); viewport.frameSelected();
            this.$nextTick(() => this.$refs.hierarchyTree?.querySelector(".tree-row.selected")?.scrollIntoView({ block: "nearest" }));
        },
        addTargetNodes(indices) {
            if (this.busy || !this.draft) return;
            try {
                const refs = targetRefsForNodes(this.document, this.selectedSet.kind, indices);
                this.targetDraft = this.selectedSet.kind === "Transform" ? refs
                    : [...this.targetDraft, ...refs.filter(ref => !this.targetDraft.some(existing => existing.Path === ref.Path))];
                if (this.selectedSet.kind === "Activation") this.draft.raw.TargetPaths = [...new Set([...(this.draft.raw.TargetPaths ?? []), ...refs.map(ref => ref.Path)])];
                this.defaultTargetMaterial();
                this.error = ""; this.status = "Targets changed — Apply keeps this change";
            } catch (error) { this.error = error.message; }
        },
        removeTarget(index) {
            if (this.busy || !this.editableTargets) return;
            const [removed] = this.targetDraft.splice(index, 1);
            if (this.selectedSet.kind === "Activation") this.draft.raw.TargetPaths = (this.draft.raw.TargetPaths ?? []).filter(path => path !== removed.Path);
            this.defaultTargetMaterial();
            this.status = "Targets changed — Apply keeps this change";
        },
        startPartDrag(event, index) {
            if (this.busy || this.mode !== "author" || event.button !== 0 || this.dragOrigin) return;
            this.dragOrigin = { index, x: event.clientX, y: event.clientY, pointerId: event.pointerId, source: event.currentTarget };
            event.currentTarget.setPointerCapture(event.pointerId);
        },
        movePartDrag(event) {
            if (!this.dragOrigin || event.pointerId !== this.dragOrigin.pointerId) return;
            if (!this.draggedNodes.length && Math.hypot(event.clientX - this.dragOrigin.x, event.clientY - this.dragOrigin.y) < 6) return;
            event.preventDefault();
            const index = this.dragOrigin.index;
            this.draggedNodes = this.selectedNodes.includes(index) ? [...this.selectedNodes] : [index];
            this.dragOrigin.x = event.clientX; this.dragOrigin.y = event.clientY;
            this.updatePartDrop(event.clientX, event.clientY);
            if (hierarchyScrollFrame === null) hierarchyScrollFrame = requestAnimationFrame(() => this.scrollPartDrag());
            this.status = "Drop between parts to reorder, on a part to reparent, or onto Targets to assign";
        },
        updatePartDrop(x, y) {
            const element = window.document.elementFromPoint(x, y);
            this.targetDragOver = !!element?.closest(".state-targets.editable");
            this.hierarchyDrop = null;
            const row = element?.closest(".tree-row");
            if (!row || !this.$refs.hierarchyTree?.contains(row)) return;
            const index = Number(row.dataset.nodeIndex), bounds = row.getBoundingClientRect();
            const offset = (y - bounds.top) / bounds.height;
            const position = offset < 0.25 ? "before" : offset > 0.75 ? "after" : "inside";
            if (hierarchyDropPlan(this.document.json, this.draggedNodes, index, position)) {
                let lineIndex = index;
                if (position === "after") {
                    const visible = this.filteredNodes, start = visible.findIndex(node => node.index === index);
                    for (let i = start + 1; start >= 0 && i < visible.length && visible[i].depth > visible[start].depth; i++) lineIndex = visible[i].index;
                }
                this.hierarchyDrop = { index, position, lineIndex };
            }
        },
        scrollPartDrag() {
            hierarchyScrollFrame = null;
            if (!this.dragOrigin || !this.draggedNodes.length) return;
            const tree = this.$refs.hierarchyTree, { x, y } = this.dragOrigin;
            if (tree) {
                const bounds = tree.getBoundingClientRect(), edge = Math.min(32, bounds.height / 3);
                if (x >= bounds.left && x <= bounds.right && y >= bounds.top && y <= bounds.bottom) {
                    const delta = y < bounds.top + edge ? -8 : y > bounds.bottom - edge ? 8 : 0;
                    if (delta) { tree.scrollTop += delta; this.updatePartDrop(x, y); }
                }
            }
            hierarchyScrollFrame = requestAnimationFrame(() => this.scrollPartDrag());
        },
        endPartDrag() {
            if (hierarchyScrollFrame !== null) cancelAnimationFrame(hierarchyScrollFrame);
            hierarchyScrollFrame = null;
            const origin = this.dragOrigin;
            if (this.draggedNodes.length) this.suppressPartClickUntil = Date.now() + 250;
            this.dragOrigin = null; this.draggedNodes = []; this.targetDragOver = false; this.hierarchyDrop = null;
            if (origin?.source.hasPointerCapture(origin.pointerId)) origin.source.releasePointerCapture(origin.pointerId);
        },
        cancelPartDrag() {
            if (this.draggedNodes.length) this.status = "Drag cancelled";
            this.endPartDrag();
        },
        async reorderParts(indices, targetIndex, position) {
            if (this.busy || this.materialEdit || !this.document || this.mode !== "author"
                || !hierarchyDropPlan(this.document.json, indices, targetIndex, position)) return false;
            if (!await this.finishOptionEdits()) return false;
            const worldMatrices = viewport.captureHierarchyTransforms?.() ?? null;
            const moved = await this.commit(doc => moveHierarchy(doc, indices, targetIndex, position, worldMatrices), { message: "Hierarchy changes saved to draft" });
            if (moved) this.expandHierarchyAncestors(indices);
            return moved;
        },
        movePartKey(event, index) {
            if (event.defaultPrevented || event.isComposing || this.busy || this.dragOrigin || this.mode !== "author") return;
            if (!event.altKey && !event.ctrlKey && !event.metaKey && !event.shiftKey
                && ["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) {
                event.preventDefault();
                const rows = this.filteredNodes, position = rows.findIndex(node => node.index === index), node = rows[position];
                if (!node) return;
                let target = node;
                if (event.key === "ArrowUp") target = rows[Math.max(0, position - 1)];
                else if (event.key === "ArrowDown") target = rows[Math.min(rows.length - 1, position + 1)];
                else if (event.key === "Home") target = rows[0];
                else if (event.key === "End") target = rows.at(-1);
                else if (event.key === "ArrowLeft") {
                    if (node.hasChildren && !this.collapsedPaths.includes(node.path)) this.toggleBranch(index);
                    else target = rows.find(row => row.index === node.parentIndex) ?? node;
                } else if (node.hasChildren) {
                    if (this.collapsedPaths.includes(node.path)) this.toggleBranch(index);
                    else if (rows[position + 1]?.parentIndex === index) target = rows[position + 1];
                }
                this.selectNode(target.index);
                this.$nextTick(() => {
                    const button = this.$refs.hierarchyTree?.querySelector(`[data-node-index="${target.index}"] .part-select`);
                    button?.focus({ preventScroll: true }); button?.scrollIntoView({ block: "nearest" });
                });
                return;
            }
            if (!event.altKey || event.ctrlKey || event.metaKey || event.shiftKey || !["ArrowUp", "ArrowDown"].includes(event.key)) return;
            event.preventDefault();
            const parent = this.document?.json.nodes.find(node => node.children?.includes(index));
            const sibling = parent?.children[parent.children.indexOf(index) + (event.key === "ArrowUp" ? -1 : 1)];
            if (sibling !== undefined) return this.reorderParts([index], sibling, event.key === "ArrowUp" ? "before" : "after");
        },
        async finishPartDrag(event) {
            if (!this.dragOrigin || event.pointerId !== this.dragOrigin.pointerId) return;
            let drop = null, indices = [], targets = false;
            if (this.draggedNodes.length) {
                event.preventDefault();
                this.updatePartDrop(event.clientX, event.clientY);
                drop = this.hierarchyDrop; indices = [...this.draggedNodes]; targets = this.targetDragOver;
                this.status = "Drag cancelled";
            }
            this.endPartDrag();
            if (targets) this.addTargetNodes(indices);
            else if (drop) return this.reorderParts(indices, drop.index, drop.position);
        },
        chooseMaterial(key) {
            const material = this.materials.find(material => material.key === key);
            if (!material) return;
            this.materialChoiceEdited = true;
            delete this.draft.raw.MaterialId; delete this.draft.raw.MaterialName; delete this.draft.raw.ShaderName;
            Object.assign(this.draft.raw, material.raw);
        },
        setMaterialSlot(index, value) {
            if (value === "all") delete this.targetDraft[index].MaterialSlot;
            else this.targetDraft[index].MaterialSlot = Number(value);
            this.defaultTargetMaterial();
        },
        defaultTargetMaterial() {
            if (this.selectedSet?.kind !== "Material" || !this.isDeferred() || this.selectedSet.options.length !== 1 || this.targetDraft.length !== 1) return;
            // A manually chosen finish in the inspector takes precedence over defaults.
            const previous = this.automaticMaterialReference ?? this.selectedOption.raw;
            if (this.materialChoiceEdited || ["MaterialId", "MaterialName", "ShaderName"].some(key => this.draft.raw[key] !== previous[key])) return;
            const target = this.targetDraft[0], material = viewport.captureMaterial(target.Path, target.MaterialSlot ?? 0);
            if (!material) return;
            delete this.draft.raw.MaterialId; delete this.draft.raw.MaterialName; delete this.draft.raw.ShaderName;
            Object.assign(this.draft.raw, material);
            this.automaticMaterialReference = clone(material);
        },
        async openMaterial(mode = "solid") {
            if (this.busy) return;
            const source = this.materials.find(material => material.key === this.materialKey);
            if (mode !== "solid" && !source) { this.error = "Choose a material first."; return; }
            await this.run("Preparing material preview…", async () => {
                const next = clone(this.document);
                next.sets[this.setIndex].options[this.optionIndex] = clone(this.draft);
                applyStateTargets(next.sets[this.setIndex], clone(this.targetDraft));
                const issues = validateDocument(next); if (issues.errors.length) throw new Error(issues.errors.join("\n"));
                await this.reloadDocument(next);
                if (!this.isDeferred()) viewport.apply(this.setIndex, this.optionIndex, true);
                const info = await viewport.beginMaterialPreview(mode === "solid" ? null : source.raw, this.targetDraft);
                this.materialEdit = { mode, key: mode === "solid" ? null : source.key, reference: mode === "solid" ? null : clone(source.raw),
                    uses: mode === "solid" ? [] : materialUses(this.document, source.key), maps: info.maps, textured: info.textured };
                this.newMaterial = { ...info, name: mode === "solid" ? this.t("New finish") : mode === "variant" ? `${source.name} ${this.t("variant")}` : source.name };
                this.materialError = ""; this.$refs.materialDialog.showModal();
            });
        },
        previewPaint() {
            try { viewport.previewMaterial(this.newMaterial); this.materialError = ""; }
            catch (error) { this.materialError = error.message; }
        },
        cancelMaterial() {
            if (this.busy) return;
            viewport.cancelMaterialPreview(); this.materialEdit = null; this.materialError = ""; this.$refs.materialDialog.close();
        },
        async createMaterial() {
            const values = clone(this.newMaterial), edit = clone(this.materialEdit), pending = clone(this.draft);
            try { validatePaint(values); } catch (error) { this.materialError = error.message; return; }
            const targets = clone(this.targetDraft);
            viewport.cancelMaterialPreview();
            const saved = await this.commit(doc => {
                const material = savePaintMaterial(doc, edit.key, values, { duplicate: edit.mode !== "edit" });
                const set = doc.sets[this.setIndex];
                if (edit.mode === "edit") {
                    doc.sets.filter(item => item.kind === "Material").forEach(item => item.options.forEach(option => {
                        const matches = Number.isInteger(material.MaterialId) ? option.raw.MaterialId === material.MaterialId
                            : option.raw.MaterialId == null && option.raw.MaterialName === material.MaterialName;
                        if (matches) { delete option.raw.ThumbnailBase64; delete option.raw.ThumbnailImageIndex; }
                    }));
                }
                const option = edit.mode === "variant" ? duplicateOption(set, pending.id) : set.options[this.optionIndex];
                option.raw = clone(pending.raw);
                delete option.raw.MaterialId; delete option.raw.MaterialName; delete option.raw.ShaderName;
                Object.assign(option.raw, material);
                if (edit.mode === "variant") option.raw.DisplayName = material.MaterialName;
                delete option.raw.ThumbnailBase64; delete option.raw.ThumbnailImageIndex;
                applyStateTargets(set, targets);
                return { optionId: option.id };
            }, { message: edit.mode === "variant" ? "Material variant saved as a new option" : "Material saved" });
            if (saved) { this.materialEdit = null; this.$refs.materialDialog.close(); }
            else {
                this.materialError = this.error;
                try { await viewport.beginMaterialPreview(edit.reference, targets); viewport.previewMaterial(values); }
                catch (error) { this.materialError = error.message; }
            }
        },
        linkValue(id) { return this.draft?.links?.find(link => link.setId === id)?.optionId ?? ""; },
        setLink(set, optionId) {
            const links = (this.draft.links ?? []).filter(link => link.setId !== set.id);
            if (optionId) links.push({ raw: {}, setId: set.id, optionId }); this.draft.links = links;
        },
        async previewDraft() {
            clearTimeout(previewTimer);
            if (this.busy || !this.draft || this.viewportDirty || this.materialEdit) return false;
            const key = this.draftKey;
            const next = clone(this.document); next.sets[this.setIndex].options[this.optionIndex] = clone(this.draft);
            try { applyStateTargets(next.sets[this.setIndex], clone(this.targetDraft)); }
            catch (error) { this.error = error.message; return false; }
            if (this.groupTimingChanged) dataOf(next.sets[this.setIndex]).TransitionTimingMode = this.groupTimingMode;
            const issues = validateDocument(next); if (issues.errors.length) { this.error = issues.errors.join("\n"); return false; }
            const showPreview = async () => {
                await this.reloadDocument(next); viewport.apply(this.setIndex, this.optionIndex, true);
                this.previewedDraftKey = key; this.previewHasEdits = this.dirty;
                this.status = "Live preview — Apply keeps these changes";
            };
            return this.run("Previewing option changes…", () => ["Camera", "Group"].includes(this.selectedSet.kind)
                ? showPreview() : preserveCameraView(viewport, showPreview));
        },
        async restartAnimation() {
            if (this.dirty) await this.previewDraft();
            else viewport.apply(this.setIndex, this.optionIndex);
            this.animationPaused = false;
        },
        toggleAnimationPause() {
            this.animationPaused = !this.animationPaused;
            viewport.getState().animationPlaybackPaused = this.animationPaused;
        },
        updateOption() {
            clearTimeout(previewTimer);
            if (this.busy || !this.draft) return false;
            const captureViewport = this.viewportDirty;
            if (captureViewport && !this.captureCurrent()) return false;
            if (!this.dirty) return captureViewport || this.previewHasEdits ? this.revertOption() : true;
            const pending = clone(this.draft);
            const targets = clone(this.targetDraft), reusePreview = this.draftKey === this.previewedDraftKey;
            const groupTimingChanged = this.groupTimingChanged, groupTimingMode = this.groupTimingMode;
            return this.commit(doc => {
                doc.sets[this.setIndex].options[this.optionIndex] = pending;
                applyStateTargets(doc.sets[this.setIndex], targets);
                if (groupTimingChanged) dataOf(doc.sets[this.setIndex]).TransitionTimingMode = groupTimingMode;
                doc.deferredStateIds = (doc.deferredStateIds ?? []).filter(id => id !== this.selectedSetId);
            }, { message: "Option updated", reusePreview, activateOption: true });
        },
        revertOption() { clearTimeout(previewTimer); return this.run("Restoring saved option…", async () => { await this.reloadDocument(this.document); if (!this.isDeferred()) viewport.apply(this.setIndex, this.optionIndex, true); this.readRuntimeState(); this.setDraft(); this.status = "Saved option restored"; }); },
        async restoreHistory(from, to) {
            if (!this.cleanRequired() || !from.length) return;
            await this.run("Restoring edit…", async () => {
                const previous = this.snapshot(), target = from.at(-1), beforeSelections = this.selections, previousSource = sourceBytes;
                this.selections = target.selections;
                sourceBytes = target.sourceBytes ?? sourceBytes;
                try { await this.reloadDocument(target.document); } catch (error) { this.selections = beforeSelections; sourceBytes = previousSource; throw error; }
                from.pop(); to.push(previous); this.document = markRaw(target.document);
                this.restorePartSelection(target.selectedNodes ?? this.selectedNodes);
                this.selectedSetId = target.selectedSetId; this.selectedOptionId = target.selectedOptionId;
                this.setDraft(); this.syncHistory(); this.unsaved = true; this.exportReady = false; this.scheduleSave(); this.status = "Edit restored";
            });
        },
        undo() { return this.optionEditsPending ? this.revertOption() : this.restoreHistory(undoStack, redoStack); }, redo() { return this.restoreHistory(redoStack, undoStack); },
        async prepareExport() {
            if (!await this.finishOptionEdits()) return;
            await this.run("Validating and reopening GLB+…", async () => {
                const issues = validateDocument(this.document);
                if (issues.errors.length) throw new Error(issues.errors.join("\n"));
                const bytes = exportDocument(sourceBytes, this.document);
                const validation = await validateBytes(bytes, { maxIssues: 50 });
                const errors = validation.issues.messages.filter(message => message.severity === 0);
                if (validation.issues.numErrors) throw new Error("GLB validation failed:\n" + errors.map(message => `${message.pointer || "Model"}: ${message.message}`).join("\n"));
                runtimeDocument = this.document;
                await viewport.load(bytes, { selections: this.previewSelections(), deferredStateIndices: deferredStateIndices(this.document) });
                const entries = [...(viewport.getState().innoWorksRuntime?.values() ?? [])];
                this.document.sets.forEach((set, index) => {
                    if (set.kind === "Unknown") return;
                    const entry = entries.find(entry => Number(entry.id.split("|").at(-2)) === index);
                    if (!entry || entry.options.length !== set.options.length) throw new Error(`${nameOf(set)} did not reopen with all its options. Check its target parts and materials.`);
                });
                this.readRuntimeState(); exportedBytes = bytes; this.exportReady = true;
                this.exportName = this.document.filename.replace(/\.glb$/i, "") + "-with-states.glb";
                this.exportSize = (bytes.length / (1024 * 1024)).toFixed(2) + " MB";
                this.exportWarnings = [...(this.document.importReport?.warnings ?? []), ...(this.document.insertedModels ?? []).flatMap(model => model.importReport?.warnings ?? []), ...issues.warnings, ...validation.issues.messages.filter(message => message.severity === 1).map(message => message.message)];
                this.$refs.exportDialog.showModal(); this.status = "GLB+ validated and reopened successfully";
            });
        },
        outputFilename() { const name = [...this.exportName.trim()].map(char => char.charCodeAt(0) < 32 || /[<>:"/\\|?*]/.test(char) ? "_" : char).join("") || "model-with-states.glb"; return name.toLowerCase().endsWith(".glb") ? name : name + ".glb"; },
        downloadExport() {
            if (!this.exportReady || !exportedBytes) return;
            const url = URL.createObjectURL(new Blob([exportedBytes], { type: "model/gltf-binary" }));
            const anchor = window.document.createElement("a"); anchor.href = url; anchor.download = this.outputFilename(); anchor.click(); setTimeout(() => URL.revokeObjectURL(url), 60000);
            this.unsaved = false; this.status = "GLB+ download started";
        },
        async openInViewer() {
            if (!this.exportReady || !exportedBytes) return;
            await this.run("Opening in Viewer…", async () => {
                try {
                    clearTimeout(autosaveTimer);
                    if (!await this.persist()) throw new Error("Save the GLB+ download first; local draft storage is unavailable.");
                    const key = await saveViewerTransfer(exportedBytes, this.outputFilename());
                    this.unsaved = false;
                    window.location.assign(new URL(`../?authoringAsset=${encodeURIComponent(key)}`, location.href).href);
                } catch (error) {
                    this.$refs.exportDialog.close();
                    throw new Error("The model could not be transferred to Viewer: " + error.message);
                }
            });
        }
    },
    async mounted() {
        this.layoutObserver = new ResizeObserver(entries => { this.workspaceWidth = entries[0].contentRect.width; });
        this.layoutObserver.observe(this.$refs.workarea);
        window.document.documentElement.lang = this.language;
        window.addEventListener("storage", event => {
            if (event.key === LANGUAGE_KEY && ["en", "ja"].includes(event.newValue)) {
                this.language = event.newValue; window.document.documentElement.lang = this.language;
            }
        });
        try {
            viewport = createViewport(this.$refs.canvas, {
                onLightingChanged: name => { this.lightingName = name; },
                onCameraChanged: () => this.refreshCameraReadout(),
                onObjectPropertiesChanged: transforms => this.refreshObjectReadout(transforms),
                onCameraEdit: () => {
                    if (this.mode === "author" && this.selectedSet?.kind === "Camera" && !this.busy) {
                        this.viewportDirty = true; this.status = "Camera changed — Apply captures this view";
                    }
                },
                onSelect: (index, additive) => this.selectNode(index, additive),
                onEdit: () => { this.viewportDirty = true; this.status = "Viewport changed — Apply captures this pose"; },
                onSelectionChanged: (key, index) => {
                    const set = runtimeDocument?.sets[Number(key.split("|").at(-2))];
                    if (set?.options[index]) this.selections[set.id] = set.options[index].id;
                }
            });
        } catch (error) { this.error = "WebGL could not start: " + error.message; return; }
        try { const recovered = await readDraft(); this.recovery = recovered ? markRaw(recovered) : null; } catch { this.persistenceStatus = "Local draft storage unavailable — export to keep your work"; }
        window.addEventListener("beforeunload", event => { if (this.unsaved || this.dirty || this.viewportDirty || this.materialEdit) { event.preventDefault(); event.returnValue = ""; } });
        window.addEventListener("keydown", event => {
            if (event.key === "Escape" && this.dragOrigin) { event.preventDefault(); this.cancelPartDrag(); return; }
            if (!event.defaultPrevented && !event.isComposing && !event.repeat && (event.ctrlKey || event.metaKey) && event.key === "Enter"
                && !event.target.closest("dialog") && this.mode === "author" && this.draft && this.optionEditsPending) {
                event.preventDefault(); this.updateOption(); return;
            }
            if (event.defaultPrevented || event.isComposing || event.target.isContentEditable || event.target.closest("input, textarea, select, dialog")) return;
            if (event.key.toLowerCase() === "f" && !event.ctrlKey && !event.metaKey && !event.altKey && !event.shiftKey
                && !event.repeat && !this.busy && this.document && this.selectedNodes.length && this.mode === "author") {
                event.preventDefault(); this.frameSelection();
            }
            if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "z") { event.preventDefault(); if (event.shiftKey) this.redo(); else this.undo(); }
            if (event.key === "Escape") { if (this.mode === "author" && this.optionEditsPending) this.revertOption(); else this.setTool("inspect"); }
        });
    },
    beforeUnmount() { this.endPartDrag(); clearTimeout(previewTimer); this.layoutObserver?.disconnect(); }
});
app.mount("#create-app");
